/**
 * First-publish hand-off (AC-8; listing-marketing-hub BR-3, BR-5): a dismissible notice above the
 * section navigation saying the listing is live only on its Resida page and not yet on Craigslist,
 * Facebook or any other site, with a primary Start marketing action to the Marketing tab, the
 * existing fee-success message and the freshly minted intake email and public link.
 * Shown only after a draft's publish completed — never on the paid-but-not-published path.
 */

import { Megaphone } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { buttonVariants } from '../../../components/ui/button-variants';
import { CopyValueBox } from './ShareCopyButtons';

interface GoLiveNoticeProps {
  message: string;
  intakeAddress: string | null;
  publicUrl: string | null;
  onDismiss: () => void;
}

export function GoLiveNotice({ message, intakeAddress, publicUrl, onDismiss }: GoLiveNoticeProps) {
  return (
    <section
      data-testid="listing-go-live-notice"
      aria-labelledby="listing-go-live-title"
      className="mb-4 rounded-lg border border-warning/25 bg-warning/10 p-4"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div role="status" className="flex min-w-0 flex-col gap-1 text-sm text-foreground">
          <h2 id="listing-go-live-title" className="text-base font-semibold text-warning">
            Published on Resida. Now get it seen.
          </h2>
          <p data-testid="listing-go-live-body">
            Your Resida listing page is the only place it is live. It is not on Craigslist, Facebook or any other site
            yet, so renters will not find it until you post it.
          </p>
          <p className="text-muted-foreground" data-testid="listing-fee-success">
            {message}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {/* A hash link: the section tabs follow `hashchange`. */}
          <a
            href="#marketing"
            data-testid="listing-go-live-start-marketing"
            className={buttonVariants({ className: 'min-h-[44px]' })}
          >
            <Megaphone aria-hidden="true" />
            Start marketing
          </a>
          <Button
            type="button"
            variant="ghost"
            className="min-h-[44px]"
            data-testid="listing-fee-success-dismiss"
            onClick={onDismiss}
          >
            Dismiss
          </Button>
        </div>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <CopyValueBox
          label="Public link"
          value={publicUrl}
          emptyText="Link pending…"
          testIdPrefix="go-live-public-link"
        />
        <CopyValueBox
          label="Intake email"
          value={intakeAddress}
          emptyText="Address pending…"
          testIdPrefix="go-live-intake-email"
        />
      </div>
    </section>
  );
}
